/**
 * Fleet — devices, sensors, quarantine, binary-auth posture.
 */
import { useMemo, useState } from "react";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import {
  useEdrAlerts,
  useEdrBinAuth,
  useEdrQuarantine,
  useEdrHealthInsights,
} from "@/hooks/security/useEdrData";
import { useDeviceAction, useQuarantineAction } from "@/hooks/security/useEdrMutations";
import { computeFleetHealth } from "@/lib/fleetHealth";
import { OwnerPill } from "@/components/owner-pill";
import { formatDistanceToNow } from "date-fns";

export default function FleetPage() {
  return (
    <ConnectorGate connectorId="edr">
      <FleetInner />
    </ConnectorGate>
  );
}

function FleetInner() {
  const alertsQ = useEdrAlerts({ limit: 500 });
  const healthQ = useEdrHealthInsights();
  const binQ = useEdrBinAuth();
  const quarQ = useEdrQuarantine();

  const alerts = alertsQ.data ?? [];
  const health = healthQ.data ?? {};
  const fleet = computeFleetHealth(health.sensorsHealthy, health.sensorsStale);
  const { healthy, stale, total, hasSensors, pct } = fleet;


  const devices = useMemo(() => aggregateDevices(alerts), [alerts]);

  return (
    <SurfaceShell
      overline="Security · Fleet"
      title="Your endpoints"
      summary={`${total} sensors enrolled. ${healthy} healthy, ${stale} stale. ${quarQ.data?.length ?? 0} files quarantined, ${binQ.data?.length ?? 0} binary-auth rules active.`}
      error={healthQ.isError ? healthQ.error : undefined}
      onRetry={healthQ.isError ? () => healthQ.refetch() : undefined}
      kpis={
        <>
          <StatTile
            label="Sensors"
            value={total}
            loading={healthQ.isLoading || healthQ.isError}
            hint={`${healthy} healthy · ${stale} stale`}
          />
          <StatTile
            label="Fleet health"
            value={fleet.value}
            loading={healthQ.isLoading || healthQ.isError}
            tone={fleet.tone}
            hint={hasSensors ? undefined : "No sensors reporting yet"}
          />
          <StatTile
            label="Quarantine"
            value={quarQ.data?.length ?? 0}
            loading={quarQ.isLoading || quarQ.isError}
            hint="Files held"
            tone={(quarQ.data?.length ?? 0) > 0 ? "warning" : "default"}
          />
          <StatTile
            label="Binary-auth rules"
            value={binQ.data?.length ?? 0}
            loading={binQ.isLoading || binQ.isError}
            hint="Allow / deny in force"
          />
        </>
      }
    >
      <Tabs defaultValue="devices">
        <TabsList>
          <TabsTrigger value="devices">Devices ({devices.length})</TabsTrigger>
          <TabsTrigger value="quarantine">Quarantine ({quarQ.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="binauth">Binary-auth ({binQ.data?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="devices" className="mt-4">
          <DevicesTab query={alertsQ} devices={devices} />
        </TabsContent>
        <TabsContent value="quarantine" className="mt-4">
          <QuarantineTab query={quarQ} items={quarQ.data ?? []} />
        </TabsContent>
        <TabsContent value="binauth" className="mt-4">
          <BinAuthTab query={binQ} rules={binQ.data ?? []} />
        </TabsContent>
      </Tabs>
    </SurfaceShell>
  );
}

type QueryLike = { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };

function aggregateDevices(alerts: any[]) {
  const m = new Map<string, { id: string; name: string; alerts: number; critical: number; lastSeen: string; consoleUser?: string | null }>();
  alerts.forEach((a) => {
    const id = a.deviceId || a.deviceName;
    if (!m.has(id)) m.set(id, { id, name: a.deviceName, alerts: 0, critical: 0, lastSeen: a.ts, consoleUser: a.consoleUser ?? null });
    const d = m.get(id)!;
    d.alerts += 1;
    if (a.severity === "critical") d.critical += 1;
    if (new Date(a.ts) > new Date(d.lastSeen)) {
      d.lastSeen = a.ts;
      // Track the most recent consoleUser observed for the device.
      if (a.consoleUser) d.consoleUser = a.consoleUser;
    }
  });
  return Array.from(m.values()).sort((a, b) => b.critical - a.critical || b.alerts - a.alerts);
}

function DevicesTab({ query, devices }: { query: QueryLike; devices: any[] }) {
  const [q, setQ] = useState("");
  const action = useDeviceAction();
  const filtered = q
    ? devices.filter((d) => `${d.id} ${d.name}`.toLowerCase().includes(q.toLowerCase()))
    : devices;

  return (
    <Panel
      title={`Devices with activity · ${filtered.length}`}
      loading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={() => query.refetch()}
      empty={!query.isLoading && !query.isError && filtered.length === 0}
      emptyTitle={devices.length === 0 ? "No active devices" : "No matches"}
      emptyDescription={
        devices.length === 0
          ? "No sensors have reported activity yet."
          : "Try clearing the filter."
      }
      skeleton="table"
      actions={
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter…"
          className="h-8 w-[220px] text-xs"
        />
      }
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Device</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Owner</th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Alerts</th>
              <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Critical</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Last activity</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2 font-mono text-foreground truncate max-w-[260px]">{d.name}</td>
                <td className="px-3 py-2">
                  <OwnerPill consoleUser={d.consoleUser} deviceId={d.id} size="sm" inert />
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{d.alerts}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {d.critical > 0 ? <span className="text-destructive font-medium">{d.critical}</span> : <span className="text-muted-foreground">0</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatDistanceToNow(new Date(d.lastSeen), { addSuffix: true })}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                    onClick={() => action.mutate({ deviceId: d.id, action: "isolate" })}>
                    Isolate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function QuarantineTab({ query, items }: { query: QueryLike; items: any[] }) {
  const action = useQuarantineAction();
  return (
    <Panel
      title={`Quarantined files · ${items.length}`}
      loading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={() => query.refetch()}
      empty={!query.isLoading && !query.isError && items.length === 0}
      emptyTitle="Quarantine is empty"
      emptyDescription="Nothing has been quarantined."
      skeleton="list"
    >
      <ul className="divide-y divide-border -mx-4 -my-4">
        {items.map((q) => (
          <li key={q.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-mono text-foreground truncate">{q.originalPath}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {q.deviceName} · {q.sha256.slice(0, 16)}… · captured {formatDistanceToNow(new Date(q.capturedAt), { addSuffix: true })}
              </div>
            </div>
            <div className="flex gap-1 flex-none">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                onClick={() => action.mutate({ itemId: q.id, action: "release" })}>
                Release
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive"
                onClick={() => action.mutate({ itemId: q.id, action: "delete" })}>
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function BinAuthTab({ query, rules }: { query: QueryLike; rules: any[] }) {
  return (
    <Panel
      title={`Binary-auth rules · ${rules.length}`}
      loading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={() => query.refetch()}
      empty={!query.isLoading && !query.isError && rules.length === 0}
      emptyTitle="No rules yet"
      emptyDescription="Allow / deny rules will appear here once configured."
      skeleton="table"
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Match</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Value</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Mode</th>
              <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Comment</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2 text-muted-foreground">{r.match}</td>
                <td className="px-3 py-2 font-mono text-foreground truncate max-w-[280px]">{r.value}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                    r.mode === "allow"
                      ? "bg-success-muted text-success-muted-foreground border-success-border"
                      : "bg-destructive/10 text-destructive border-destructive/30"
                  }`}>{r.mode}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[300px]">{r.comment ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
